import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { TraxWorkOrder, TraxImportResult } from "../../api/types";
import { S } from "../../styles";

type PullStep = "idle" | "selecting" | "done";

export function PullTraxPanel({ onSuccess }: { onSuccess: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<PullStep>("idle");
  const [workOrders, setWorkOrders] = useState<TraxWorkOrder[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<TraxImportResult | null>(null);

  const fetchMutation = useMutation({
    mutationFn: api.listTraxWorkOrders,
    onSuccess: (r) => {
      setWorkOrders(r.work_orders);
      setSelected(
        new Set(
          r.work_orders
            .filter((w) => w.import_status === "new")
            .map((w) => w.work_order_ref),
        ),
      );
      setStep("selecting");
    },
  });

  const importMutation = useMutation({
    mutationFn: (refs: string[]) => api.importTraxWorkOrders(refs),
    onSuccess: (r) => {
      setResult(r);
      setStep("done");
      void queryClient.invalidateQueries({ queryKey: ["rfqs"] });
      if (r.imported > 0) setTimeout(onSuccess, 1500);
    },
  });

  const reset = () => {
    setStep("idle");
    setWorkOrders([]);
    setSelected(new Set());
    setResult(null);
    fetchMutation.reset();
    importMutation.reset();
  };

  const toggleOne = (ref: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  const importableRefs = workOrders
    .filter((w) => w.import_status === "new")
    .map((w) => w.work_order_ref);
  const allSelected =
    importableRefs.length > 0 && importableRefs.every((r) => selected.has(r));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(importableRefs));
  };

  const statusBadge = (wo: TraxWorkOrder) => {
    if (wo.import_status === "already_imported")
      return (
        <span
          style={{
            fontSize: 11,
            padding: "1px 6px",
            borderRadius: 3,
            background: "#e3fafc",
            color: "#1098ad",
            fontWeight: 600,
          }}
        >
          Already imported
        </span>
      );
    if (wo.import_status === "no_part")
      return (
        <span
          style={{
            fontSize: 11,
            padding: "1px 6px",
            borderRadius: 3,
            background: "#fff0f0",
            color: "#c92a2a",
            fontWeight: 600,
          }}
        >
          No part record
        </span>
      );
    return null;
  };

  const resultIcon = (s: string) =>
    s === "imported" ? "✓" : s === "error" ? "✗" : "–";
  const resultColor = (s: string) =>
    s === "imported" ? "#2f9e44" : s === "error" ? "#c92a2a" : "#888";

  return (
    <div style={S.card}>
      <h2 style={S.h2}>Import Work Orders from Trax ERP</h2>
      <p style={S.muted}>
        Fetch open work orders from Trax, select which ones to import, then
        create Requirements and dispatch RFQs for each selected order.
      </p>

      {fetchMutation.error && (
        <p style={S.err}>⚠ {fetchMutation.error.message}</p>
      )}
      {importMutation.error && (
        <p style={S.err}>⚠ {importMutation.error.message}</p>
      )}

      {step === "idle" && (
        <button
          style={S.btn}
          onClick={() => fetchMutation.mutate()}
          disabled={fetchMutation.isPending}
        >
          {fetchMutation.isPending
            ? "Fetching…"
            : "⬇ Fetch Work Orders from Trax"}
        </button>
      )}

      {step === "selecting" && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 13, color: "#555" }}>
              {workOrders.length} work order{workOrders.length !== 1 ? "s" : ""}{" "}
              from Trax ·{" "}
              <strong style={{ color: "#2f9e44" }}>
                {importableRefs.length} new
              </strong>
              {" · "}
              <span style={{ color: "#888" }}>{selected.size} selected</span>
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={S.btnGhost} onClick={reset}>
                ↺ Reset
              </button>
              <button
                style={{
                  ...S.btn,
                  padding: "7px 16px",
                  opacity: selected.size === 0 ? 0.5 : 1,
                }}
                onClick={() => importMutation.mutate([...selected])}
                disabled={selected.size === 0 || importMutation.isPending}
              >
                {importMutation.isPending
                  ? "Importing…"
                  : `✓ Import ${selected.size} Selected`}
              </button>
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e8eaf0",
              borderRadius: 8,
              overflow: "hidden",
              fontSize: 13,
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f4f5f9" }}>
                  <th style={{ padding: "8px 12px", width: 36 }}>
                    {importableRefs.length > 0 && (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        title="Select all importable"
                      />
                    )}
                  </th>
                  {[
                    "Work Order",
                    "Part",
                    "Qty",
                    "Condition",
                    "AOG",
                    "Status",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#555",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {workOrders.map((wo) => {
                  const isImportable = wo.import_status === "new";
                  return (
                    <tr
                      key={wo.work_order_ref}
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        background: isImportable ? "#fff" : "#fafafa",
                        opacity: isImportable ? 1 : 0.6,
                      }}
                    >
                      <td style={{ padding: "8px 12px" }}>
                        {isImportable && (
                          <input
                            type="checkbox"
                            checked={selected.has(wo.work_order_ref)}
                            onChange={() => toggleOne(wo.work_order_ref)}
                          />
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      >
                        {wo.work_order_ref}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <div style={{ fontWeight: 500 }}>{wo.part_number}</div>
                        {wo.part_description && (
                          <div style={{ fontSize: 11, color: "#888" }}>
                            {wo.part_description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "8px 12px" }}>{wo.quantity}</td>
                      <td style={{ padding: "8px 12px" }}>
                        {wo.condition_code}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {wo.is_aog ? (
                          <span style={{ color: "#c0392b", fontWeight: 700 }}>
                            ⚡ AOG
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ padding: "8px 12px" }}>{statusBadge(wo)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div>
          <div
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 12,
              padding: "10px 14px",
              background: result.imported > 0 ? "#f0fff4" : "#fff5f5",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <span>
              ✓ Imported:{" "}
              <strong style={{ color: "#2f9e44" }}>{result.imported}</strong>
            </span>
            <span>
              Skipped:{" "}
              <strong style={{ color: "#888" }}>{result.skipped}</strong>
            </span>
            <span style={{ color: "#555" }}>{result.message}</span>
          </div>

          <div
            style={{
              border: "1px solid #e8eaf0",
              borderRadius: 8,
              overflow: "hidden",
              fontSize: 12,
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f4f5f9" }}>
                  {["Work Order", "Part", "Result", "Details"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "6px 12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#555",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.details.map((d) => (
                  <tr
                    key={d.work_order_ref}
                    style={{ borderTop: "1px solid #f0f0f0" }}
                  >
                    <td
                      style={{
                        padding: "6px 12px",
                        fontFamily: "monospace",
                        fontWeight: 500,
                      }}
                    >
                      {d.work_order_ref}
                    </td>
                    <td style={{ padding: "6px 12px" }}>{d.part_number}</td>
                    <td style={{ padding: "6px 12px" }}>
                      <span
                        style={{
                          fontWeight: 700,
                          color: resultColor(d.status),
                        }}
                      >
                        {resultIcon(d.status)} {d.status}
                      </span>
                    </td>
                    <td style={{ padding: "6px 12px", color: "#666" }}>
                      {d.rfq_id ? (
                        <span>
                          RFQ:{" "}
                          <span style={{ fontFamily: "monospace" }}>
                            {d.rfq_id.slice(0, 8)}…
                          </span>
                        </span>
                      ) : (
                        (d.reason ?? "—")
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button style={{ ...S.btnGhost, marginTop: 12 }} onClick={reset}>
            ↺ Import More
          </button>
        </div>
      )}
    </div>
  );
}
